import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { CriterionChooser } from '@/components/admin/protocoles/step/criterion-chooser';
import type { CriterionCard } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import type { ReactNode } from 'react';
import { useState } from 'react';

/**
 * The frame of a « l'un de » group of criteria (issue #181, scenario-succes-
 * conduite.md §7; maquette `EditeurObjectifApres.html`, `.grpA`).
 *
 * The group is drawn as a titled box around its lines, with the « ou » joints
 * between them (the caller passes both as `children`): a line inside is filled
 * as soon as ONE of them is, which is what the box says and what a flat list of
 * required lines cannot.
 *
 * A group of a SINGLE criterion is the refused state (the version will not be
 * saved with it): the box goes yellow, and « Délier » - the one gesture the lone
 * member has, since it has no « ou » above it - turns the line back into a plain
 * required one. The editor itself never writes that state (removing a criterion
 * settles the group it leaves); a protocol written by hand or by the assistant
 * can still carry it, and it must be seen rather than silently repaired.
 *
 * « Ajouter au groupe » is the foot of the box: the criterion picked joins as
 * one more member, under the last one, so the members stay neighbours.
 */

export interface OrGroupFrameProps {
  /** The shared label: the frame carries it as `data-or-group`, for the tests. */
  orGroup: string;
  /** True for a group of fewer than two criteria: the yellow, refused state. */
  refused: boolean;
  readOnly: boolean;
  /** The library and what the objective already cites, for « Ajouter au groupe ». */
  criteria: readonly CriterionCard[];
  citedIds: readonly string[];
  onAdd: (criterionId: string) => void;
  /** « Délier » of a group of one: the lone member becomes a plain line. */
  onRelease: () => void;
  children: ReactNode;
}

export function OrGroupFrame({
  orGroup,
  refused,
  readOnly,
  criteria,
  citedIds,
  onAdd,
  onRelease,
  children,
}: OrGroupFrameProps) {
  const [chooser, setChooser] = useState(false);
  /* The chooser is placed on this button, so it is held as an element. */
  const [addEl, setAddEl] = useState<HTMLButtonElement | null>(null);
  return (
    <div
      data-testid={PROTOCOL_TEST_IDS.criterionGroup}
      data-or-group={orGroup}
      className={`relative mt-2 mb-0.5 rounded-md border-2 pt-3.5 pb-1 pl-2.5 ${
        refused ? 'border-jaune bg-jaune-tint' : 'border-bleu-nuit-tint'
      }`}
    >
      <span
        className={`-top-2.5 absolute left-2.5 bg-gray-50 px-1.5 font-semibold text-micro uppercase leading-4.5 tracking-wide ${
          refused ? 'text-jaune-deep' : 'text-bleu-nuit'
        }`}
      >
        l’un de
      </span>
      {refused && !readOnly ? (
        <button
          type="button"
          data-testid={PROTOCOL_TEST_IDS.criterionGroupRelease}
          onClick={onRelease}
          className="-top-2.5 absolute right-2.5 cursor-pointer bg-jaune-tint px-1.5 text-caption text-jaune-deep underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          Délier
        </button>
      ) : null}
      {children}
      {readOnly ? null : (
        <div className="flex pb-1">
          <button
            ref={setAddEl}
            type="button"
            data-testid={PROTOCOL_TEST_IDS.criterionGroupAdd}
            aria-expanded={chooser}
            onClick={() => setChooser((was) => !was)}
            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2.5 font-semibold text-bleu-nuit text-caption hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            <Icon name="add" size={14} />
            Ajouter au groupe
          </button>
          {chooser ? (
            <CriterionChooser
              criteria={criteria}
              citedIds={citedIds}
              anchor={addEl}
              onClose={() => setChooser(false)}
              onPick={(criterionId) => {
                setChooser(false);
                onAdd(criterionId);
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
